import React from 'react';
import styled, { css } from 'styled-components/macro';

import media from 'helpers/media';
import Quote from 'assets/images/black-friday/quote.svg';

import { Column, LegalLinks, Typography as T } from 'components';
import { ColorVariants } from 'theme';

const PlanCommonBF = css`
    & [class*='PlanHeadline'] > p {
        font-size: 1.25rem;
        line-height: 100%;
    }

    & [class*='PlanRoot'] {
        max-width: 320px;
        margin: auto;
        ${media.fromLaptop`max-width: 100%;`}
    }
`;

export const ColumnStyled = styled(({ $order, ...props }) => (
    <Column {...props} />
))<{
    $order?: number;
}>`
    margin-top: ${({ theme }) => theme.space.large};
    margin-bottom: ${({ theme }) => theme.space.large};

    ${({ $order }) => media.toTablet`order: ${$order};`}
`;

export const SectionConversion = styled.section<{ $bg: string }>`
    padding-bottom: ${({ theme }) => theme.space.base};
    background-image: ${({ $bg }) => `url('${$bg}');`};
    background-size: cover;
    background-position: center;

    ${PlanCommonBF}
`;

export const DesktopHero = styled.div<{ $bg: string }>`
    ${({ $bg }) => media.fromLaptop`
        background-size: cover;
        background-position: center;
        background-image: linear-gradient(rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.71) 0%), url('${$bg}');
    `}
`;

export const SectionPlans = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;

    max-width: 1500px;
    margin: auto;
    background-color: ${({ theme }) => theme.colors.labels};

    ${({ theme }) => media.fromLaptop`
        flex-wrap: nowrap;
        justify-content: space-between;
        background-color: transparent;
        padding: ${theme.space.xgiant} ${theme.space.medium};

        [class*="Grid__Row"] {
            flex-wrap: nowrap;
        }
    `}
`;

export const ColumnHeaders = styled.div<{ $bg?: string }>`
    max-width: 610px;
    ${({ theme, $bg }) => media.toTablet`
        width: 100%;
        max-width: 100%;
        padding: ${theme.space.base};
        padding-bottom: 150px;
        background-color: ${theme.colors.darkGrey};

        ${
            $bg &&
            css`
                background-size: cover;
                background-position: right;
                background-image: linear-gradient(rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 0%), url('${$bg}');
            `
        }
    `}
`;

export const ColumnPlans = styled.div`
    width: 100%;
    padding: 0 ${({ theme }) => theme.space.medium};
    margin-top: -135px;

    ${media.fromLaptop`margin-top: 0;`}

    ${PlanCommonBF}
`;

export const ContentQuote = styled.div`
    padding: ${({ theme }) => `${theme.space.large} ${theme.space.medium}`};
    color: ${({ theme }) => theme.colors.baseGray};

    position: relative;
    width: 75%;
    margin: auto;
    text-align: center;

    &:before,
    &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    &:before {
        background: url(${Quote}) no-repeat;
    }

    &:after {
        background: url(${Quote}) no-repeat;
        transform: rotate(180deg);
    }
`;

export const LegalLinksConversion = styled((props) => (
    <LegalLinks theme="light" {...props} />
))`
    margin-top: 120px;
    ${({ theme }) => media.mobile`margin-top: ${theme.space.large}`};
`;

export const ImageContainer = styled.div<{
    leftMargin?: boolean;
    rightMargin?: boolean;
}>`
    ${({ theme, leftMargin }) =>
        leftMargin &&
        css`
            margin-left: ${theme.space.large};
        `}

    ${({ theme, rightMargin }) =>
        rightMargin &&
        css`
            margin-right: ${theme.space.large};
        `}

    ${media.toTablet`
        margin-left: 0;
        margin-right: 0;
    `}

    & > img {
        width: 100%;
    }
`;

const CommonCopiesStyles = css`
    margin-top: ${({ theme }) => theme.space.base};

    ${({ theme }) => media.toTablet`
        font-size: 1rem;
        margin-top: ${theme.space.small};
    `}
`;

export const Description = styled((props) => <T {...props} />)`
    ${CommonCopiesStyles}
`;

export const Header = styled((props) => <T {...props} />)<{
    $color: ColorVariants;
}>`
    ${media.toTablet`font-size: 2rem;`}

    ${({ theme, $color }) =>
        $color &&
        css`
            & > span {
                color: ${theme.colors[$color]};
            }
        `}
`;

export const LearnMoreLink = styled.a`
    display: inline-block;
    color: white;
    font-size: 1.125rem;

    cursor: pointer;
    text-decoration: underline;

    &:hover {
        color: ${({ theme }) => theme.colors.brand};
    }

    ${CommonCopiesStyles}
`;
